import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { failureOf, said } from '@/api/errors';
import {
  useAddTaskNote,
  useCloseTask,
  useDecideHeld,
  useElaborateTask,
  useProposeTaskTime,
  useScheduleTask,
  useTask,
  useTaskNotes,
  useUpdateTask,
  type TaskEditInput,
  useTaskStanding,
} from '@/api/hooks';
import { StandingPanel } from '@/ui/Standing';
import type { WireTask, WireTaskNote } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { PageHeader, useFrameMaybe } from '@/app/frame';
import { Segment, Button, Field, Kicker, Skeleton, TextInput, Badge } from '@/ui/primitives';
import { RecordHeader, when } from '@/ui/records';
import { ToolLabel, Technical } from '@/app/technical';
import { Select, Textarea } from '@/ui/fields';
import { useToast } from '@/ui/Toast';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { explainTask } from './explain';
import { TaskBoard } from './Board';
import { TaskReviews } from './Reviews';
import { Reassign } from './Assign';
import { RemoveDialog } from './Remove';
import { CallOutcome } from '@/validation/FollowThrough';
import { TwinTaskPage, TwinTasks } from './TwinTasks';

/**
 * Tasks (ADR-0149): the board of the person's work, and their twin's tasks
 * beside it. A task's own page is where it is changed, discussed, made
 * clearer by the department's agent, and put in the calendar.
 */

export function Tasks() {
  const [tab, setTab] = useState<'board' | 'twin'>(() => {
    try {
      return localStorage.getItem('norum.tasks.tab') === 'twin' ? 'twin' : 'board';
    } catch {
      return 'board';
    }
  });
  const choose = (next: 'board' | 'twin') => {
    setTab(next);
    try {
      localStorage.setItem('norum.tasks.tab', next);
    } catch {
      // No memory: the tab resets next time, which is harmless.
    }
  };
  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 p-6 phone:p-4">
      <PageHeader kicker="Mine" title="Tasks" />
      <Segment
        label="Which tasks"
        value={tab}
        options={[
          { value: 'board', label: 'Board' },
          { value: 'twin', label: 'Your twin’s tasks' },
        ]}
        onChoose={choose}
      />
      {tab === 'board' ? <><TaskReviews /><TaskBoard /></> : <TwinTasks embedded />}
    </div>
  );
}

/** `/tasks/:taskId`: a twin's task has its own page; every other task this one. */
export function TaskRoute({ recordId }: { recordId?: string } = {}) {
  const params = useParams();
  const taskId = recordId ?? params.taskId ?? '';
  const q = useTask(taskId);
  if (q.isPending) return <Skeleton rows={10} />;
  // A twin's task is read through its own door (§16): the person's, and
  // theirs alone. A task this door does not show may still be one.
  if (q.isError || q.data.task.procedure === 'twin_task') return <TwinTaskPage recordId={taskId} />;
  return <TaskPage task={q.data.task} />;
}

const STATE_WORDS: Record<WireTask['state'], { label: string; tone: 'neutral' | 'accent' | 'warning' | 'current' }> = {
  open: { label: 'TO DO', tone: 'neutral' },
  in_progress: { label: 'DOING', tone: 'accent' },
  waiting: { label: 'WAITING', tone: 'warning' },
  done: { label: 'DONE', tone: 'current' },
  cancelled: { label: 'CANCELLED', tone: 'neutral' },
};

const dayOf = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');

export function TaskPage({ task }: { task: WireTask }) {
  const me = useMeOrThrow();
  const who = useWho();
  const frame = useFrameMaybe();
  const toast = useToast();
  const close = useCloseTask();
  const closed = task.state === 'done' || task.state === 'cancelled';
  const agentOrProcedure = Boolean(task.procedure || task.assigneeAgent);
  // A request's words are the asking department's (§18); an agent's task is
  // the agent's until it hands it back.
  const wordsLocked = !task.mayEdit || closed || Boolean(task.request) || task.originKind === 'request' || agentOrProcedure;
  const mine = task.assigneeHuman === me.humanId;
  const failed = (error: unknown) => toast({ text: said(failureOf(error)), tone: 'danger' });
  const [removing, setRemoving] = useState(false);
  const removable = task.mayEdit && !closed && !task.request && task.originKind !== 'request' && !(task.assigneeAgent && task.state === 'in_progress');
  // Where an agent's task stands and why, while it is not closed (ADR-0169).
  const standing = useTaskStanding(task.id, Boolean(task.assigneeAgent) && !closed);
  return (
    <div className="mx-auto flex w-full max-w-[960px] flex-col gap-6 p-6 phone:p-4" data-testid="task-page">
      {removing && <RemoveDialog target={{ taskId: task.id }} title={task.title} onClose={() => setRemoving(false)} />}
      <RecordHeader
        kicker="Task"
        title={task.title}
        meta={`Made ${when(task.createdAt)}${task.completedAt ? ` · closed ${when(task.completedAt)}` : ''}`}
        chips={[STATE_WORDS[task.state]]}
        back={{ to: '/tasks', label: 'Tasks' }}
        ask={false}
        actions={
          <>
            {frame && (
              <Button variant="primary" onClick={() => frame.ask('', { page: 'task', taskId: task.id })}>
                Discuss in the chat
              </Button>
            )}
            {task.mayEdit && !closed && !task.request && task.originKind !== 'request' && !(task.assigneeAgent && task.state === 'in_progress') && (
              <Button icon="check"
                onClick={() => close.mutate({ taskId: task.id, state: 'done' }, { onSuccess: () => toast({ text: 'Marked done.' }), onError: failed })}
                disabled={close.isPending}
              >Mark done</Button>
            )}
            {removable && <Button icon="trash" onClick={() => setRemoving(true)}>Remove</Button>}
          </>
        }
      />
      {/* What it is, where it came from, who owns it, and what to do: the same words as the board's card (ADR-0155). */}
      <Explainer
        startOpen
        label="About this task"
        context={explainTask(task, who, new Date(), {
          meId: me.humanId,
          onPage: true,
          ...(mine && !closed && !task.request ? { markDone: () => close.mutate({ taskId: task.id, state: 'done' }, { onSuccess: () => toast({ text: 'Marked done.' }), onError: failed }), busy: close.isPending } : {}),
        })}
      />
      {standing.data && <StandingPanel standing={standing.data.standing} />}
      {task.request && (
        <p className="m-0 border border-line bg-sunken p-3 text-dense text-secondary">
          Another department asked for this. Its words are theirs: answer it or say no from the request, and say what you need in the notes below.
        </p>
      )}
      {task.held && !closed && <HeldProposal task={task} held={task.held} />}
      <Details task={task} locked={wordsLocked} />
      {task.mayEdit && !closed && !task.request && task.originKind !== 'request' && !task.procedure && !(task.assigneeAgent && task.state === 'in_progress') && <Reassign task={task} />}
      {task.callStep && <CallOutcome taskId={task.id} step={task.callStep} />}
      {(task.waitingOn?.length ?? 0) > 0 && (
        <p className="m-0 border border-line bg-sunken p-3 text-dense text-secondary">
          Waits on {task.waitingOn!.map((b) => `“${b.title}”`).join(', ')}, which {task.waitingOn!.length === 1 ? 'is' : 'are'} not done yet.
        </p>
      )}
      {mine && !closed && <Calendar task={task} />}
      <Notes task={task} canAdopt={!wordsLocked} />
    </div>
  );
}

/**
 * What an agent proposed at open_loop, for the lead to decide in one press
 * (ADR-0151). Approving runs the calls in order, as the agent, and stops at
 * the first refusal; declining calls nothing. Each call is named in words;
 * what it was given is the superadmin's to read.
 */
function HeldProposal({ task, held }: { task: WireTask; held: NonNullable<WireTask['held']> }) {
  const decide = useDecideHeld();
  const toast = useToast();
  const name = held.agent.split('@')[0]!.split('.')[0]!;
  const agent = name.charAt(0).toUpperCase() + name.slice(1);
  const press = (decision: 'approved' | 'declined') =>
    decide.mutate(
      { taskId: task.id, decision },
      {
        onSuccess: () => toast({ text: decision === 'approved' ? `Approved. ${agent} does it now, in this order, and stops at anything refused.` : 'Declined. Nothing was done.' }),
        onError: (error) => toast({ text: said(failureOf(error)), tone: 'danger' }),
      },
    );
  return (
    <section className="flex flex-col gap-3 border border-line bg-sunken p-4" data-testid="held-proposal">
      <Kicker>What {agent} proposed</Kicker>
      <ol className="m-0 flex list-decimal flex-col gap-2 pl-5">
        {held.calls.map((c, i) => (
          <li key={i} className="text-dense">
            <ToolLabel id={c.toolId} />
            <Technical>
              <pre className="m-0 mt-1 overflow-x-auto bg-page p-2 text-meta">{JSON.stringify(c.args, null, 2)}</pre>
            </Technical>
          </li>
        ))}
      </ol>
      <p className="m-0 text-dense text-secondary">
        Your department acts only on approval for now. Approve, and {agent} does these in this order and stops at the first one refused; decline, and nothing is done.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button icon="check" variant="primary" disabled={decide.isPending} onClick={() => press('approved')}>Approve and run</Button>
        <Button icon="close" disabled={decide.isPending} onClick={() => press('declined')}>Decline</Button>
      </div>
    </section>
  );
}

function Details({ task, locked }: { task: WireTask; locked: boolean }) {
  const toast = useToast();
  const update = useUpdateTask();
  const [title, setTitle] = useState(task.title);
  const [body, setBody] = useState(task.body ?? '');
  const [priority, setPriority] = useState(String(task.priority));
  const [due, setDue] = useState(dayOf(task.dueAt));
  const [estimate, setEstimate] = useState(task.estimateMinutes ? String(task.estimateMinutes) : '');
  useEffect(() => {
    setTitle(task.title);
    setBody(task.body ?? '');
    setPriority(String(task.priority));
    setDue(dayOf(task.dueAt));
    setEstimate(task.estimateMinutes ? String(task.estimateMinutes) : '');
  }, [task.title, task.body, task.priority, task.dueAt, task.estimateMinutes]);

  const edit: TaskEditInput = { taskId: task.id };
  if (title.trim() !== task.title) edit.title = title;
  if (body.trim() !== (task.body ?? '').trim()) edit.body = body.trim() === '' ? null : body;
  if (Number(priority) !== task.priority) edit.priority = Number(priority);
  if (due !== dayOf(task.dueAt)) edit.dueAt = due ? new Date(`${due}T17:00:00`).toISOString() : null;
  const minutes = estimate.trim() === '' ? null : Number(estimate);
  if (minutes !== (task.estimateMinutes ?? null)) edit.estimateMinutes = minutes;
  const dirty = Object.keys(edit).length > 1;

  return (
    <section className="flex flex-col gap-4 border border-line p-4" aria-label="What the task is">
      <Kicker>What it is</Kicker>
      <Field label="Title">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} disabled={locked} maxLength={200} />
      </Field>
      <Field label="Description" hint="What is meant, what done looks like, anything the next person needs. Elaborate below asks the agent for a first draft.">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} disabled={locked} rows={6} maxLength={8000} />
      </Field>
      <div className="grid grid-cols-3 gap-3 phone:grid-cols-1">
        <Field label="Priority">
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            disabled={locked}
            options={[
              { value: '1', label: '1 · first' },
              { value: '2', label: '2' },
              { value: '3', label: '3 · usual' },
              { value: '4', label: '4' },
              { value: '5', label: '5 · last' },
            ]}
          />
        </Field>
        <Field label="Due">
          <TextInput type="date" value={due} onChange={(e) => setDue(e.target.value)} disabled={locked} />
        </Field>
        <Field label="Estimate, minutes">
          <TextInput type="number" min={5} max={480} step={5} value={estimate} onChange={(e) => setEstimate(e.target.value)} disabled={locked} />
        </Field>
      </div>
      {!locked && (
        <div className="flex gap-2">
          <Button
            variant="primary"
            disabled={!dirty || update.isPending}
            onClick={() => update.mutate(edit, { onSuccess: () => toast({ text: 'Saved.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}
          >
            Save
          </Button>
        </div>
      )}
    </section>
  );
}

function Calendar({ task }: { task: WireTask }) {
  const toast = useToast();
  const propose = useProposeTaskTime();
  const schedule = useScheduleTask();
  const [choice, setChoice] = useState<string | null>(null);
  const [pickDay, setPickDay] = useState('');
  const [pickTime, setPickTime] = useState('09:00');
  const minutes = task.estimateMinutes ?? 30;
  const failed = (error: unknown) => toast({ text: said(failureOf(error)), tone: 'danger' });
  const put = (from: Date) =>
    schedule.mutate(
      { taskId: task.id, from: from.toISOString(), to: new Date(from.getTime() + minutes * 60_000).toISOString() },
      {
        onSuccess: (r) => toast({ text: r.onGoogle ? 'In your calendar, as a hold only you can see until you send it.' : `Held here. ${r.notOnGoogle ?? ''}`.trim() }),
        onError: failed,
      },
    );

  if (task.hold) {
    const from = new Date(task.hold.startsAt);
    const to = new Date(task.hold.endsAt);
    return (
      <section className="flex flex-col gap-2 border border-line p-4" aria-label="In the calendar">
        <Kicker>In the calendar</Kicker>
        <p className="m-0 text-dense">
          {from.toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}–
          {to.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </p>
        <p className="m-0 text-dense text-meta">Move or let go of it from your calendar on Today.</p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3 border border-line p-4" aria-label="Put it in the calendar">
      <Kicker>Put it in the calendar</Kicker>
      <p className="m-0 text-dense text-secondary">
        {minutes} minutes{task.estimateMinutes ? '' : ' (no estimate yet, so half an hour)'}, held on your calendar. Nobody else is told.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => propose.mutate({ taskId: task.id }, { onError: failed })} disabled={propose.isPending}>
          Propose a time
        </Button>
      </div>
      {propose.data && propose.data.slots.length === 0 && (
        <p className="m-0 text-dense text-meta">No free time in your working hours in the next fortnight. Pick one yourself below.</p>
      )}
      {propose.data && propose.data.slots.length > 0 && (
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="text-micro uppercase tracking-label text-meta">Free times that fit</legend>
          {propose.data.slots.map((s) => (
            <label key={s.from} className="flex min-h-tap items-center gap-2 text-dense">
              <input type="radio" name={`slot-${task.id}`} value={s.from} checked={choice === s.from} onChange={() => setChoice(s.from)} />
              {new Date(s.from).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </label>
          ))}
          <div>
            <Button variant="primary" disabled={!choice || schedule.isPending} onClick={() => choice && put(new Date(choice))}>
              Put it there
            </Button>
          </div>
        </fieldset>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Or a day">
          <TextInput type="date" value={pickDay} onChange={(e) => setPickDay(e.target.value)} />
        </Field>
        <Field label="At">
          <TextInput type="time" value={pickTime} onChange={(e) => setPickTime(e.target.value)} step={900} />
        </Field>
        <Button disabled={!pickDay || schedule.isPending} onClick={() => put(new Date(`${pickDay}T${pickTime}:00`))}>
          Hold that time
        </Button>
      </div>
    </section>
  );
}

function Notes({ task, canAdopt }: { task: WireTask; canAdopt: boolean }) {
  const toast = useToast();
  const [asked, setAsked] = useState<number | null>(null);
  const waiting = asked !== null && Date.now() - asked < 90_000;
  const q = useTaskNotes(task.id, waiting);
  const add = useAddTaskNote();
  const elaborate = useElaborateTask();
  const update = useUpdateTask();
  const [text, setText] = useState('');
  const notes = q.data?.notes ?? [];
  const lastElaboration = [...notes].reverse().find((n) => n.kind === 'elaboration' && !n.held);
  // An elaboration that arrived after the press ends the wait.
  useEffect(() => {
    if (asked !== null && lastElaboration && new Date(lastElaboration.createdAt).getTime() >= asked - 5_000) setAsked(null);
  }, [asked, lastElaboration]);
  const failed = (error: unknown) => toast({ text: said(failureOf(error)), tone: 'danger' });
  const closed = task.state === 'done' || task.state === 'cancelled';

  return (
    <section className="flex flex-col gap-3 border border-line p-4" aria-label="Discussion">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Kicker>Discussion</Kicker>
        {!closed && (
          <Button
            onClick={() =>
              elaborate.mutate(
                { taskId: task.id },
                { onSuccess: () => { setAsked(Date.now()); toast({ text: 'Asked the agent to elaborate. It appears here in a moment.' }); }, onError: failed },
              )
            }
            disabled={elaborate.isPending || waiting}
          >
            {waiting ? 'Elaborating…' : 'Elaborate'}
          </Button>
        )}
      </div>
      {q.isPending && <Skeleton rows={2} />}
      {notes.length === 0 && !q.isPending && (
        <p className="m-0 text-dense text-meta">Nothing said yet. Say what is unclear, or ask the agent to elaborate: steps, what done looks like, and what to settle first.</p>
      )}
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {notes.map((n) => (
          <Note key={n.id} note={n} canAdopt={canAdopt} onAdopt={() => update.mutate({ taskId: task.id, body: n.body }, { onSuccess: () => toast({ text: 'That is the task’s description now.' }), onError: failed })} />
        ))}
      </ol>
      {!closed && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const body = text.trim();
            if (!body) return;
            add.mutate(
              { taskId: task.id, body },
              {
                onSuccess: (r) => {
                  setText('');
                  if (r.held) toast({ text: 'Kept, but the scanner held it, so it is not shown or read by an agent.', tone: 'danger' });
                },
                onError: failed,
              },
            );
          }}
        >
          <Field label="Say something about it">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={8000} />
          </Field>
          <div>
            <Button type="submit" disabled={!text.trim() || add.isPending}>
              Say it
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

function Note({ note, canAdopt, onAdopt }: { note: WireTaskNote; canAdopt: boolean; onAdopt: () => void }) {
  return (
    <li className={`flex flex-col gap-2 border-l-2 pl-3 ${note.kind === 'elaboration' ? 'border-accent' : 'border-line'}`} data-testid="task-note">
      <div className="flex flex-wrap items-center gap-2 text-micro text-meta">
        <span className="font-medium text-ink">{note.authorName}</span>
        {note.kind === 'elaboration' && <Badge plain tone="accent">Elaboration</Badge>}
        <span>{when(note.createdAt)}</span>
      </div>
      {note.held ? (
        <p className="m-0 text-dense italic text-meta">Held by the scanner: kept, not shown, and never read by an agent.</p>
      ) : (
        <p className="m-0 whitespace-pre-wrap text-dense">{note.body}</p>
      )}
      {note.kind === 'elaboration' && !note.held && canAdopt && (
        <div>
          <Button size="row" onClick={onAdopt}>
            Use this as the description
          </Button>
        </div>
      )}
    </li>
  );
}
